import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DropZone from 'components/dropzone/DropZone';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import { passwordInput } from 'components/final-form/PasswordInput';
import { SelectFieldProps, selectInput } from 'components/final-form/SelectInput';
import { InputFieldProps, textInput } from 'components/final-form/TextInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import EpUserRolesSegment from 'components/user-settings/segments/EpUserRolesSegment';
import UserRolesSegment from 'components/user-settings/segments/UserRolesSegment';
import { FormApi } from 'final-form';
import React, { Component } from 'react';
import { FileError, FileRejection } from 'react-dropzone';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Header, Loader } from 'semantic-ui-react';
import { getAllAccountRoles } from 'services/accountServices';
import { getAllEpRoles } from 'services/epRoleServices';
import { getAllMandates } from 'services/mandateServices';
import { getAllRoles } from 'services/roleServices';
import { addUser, editUser } from 'services/userSettingsServices';
import styled from 'styled-components';
import { EpRoleDto, EpUserRoleDto, RoleDto, UpsertUserSettingsDto, UserRoleDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  user?: UpsertUserSettingsDto,
  userId?: number,
  accountId?: number,
  treaters: Array<DropdownOption>
}

interface Values {
  password: string,
  confirm: string
}

const StyledField = styled(Field)`
  margin: 0 !important;
  padding: 0 !important;
`;

interface Errors {
  confirm?: string
}

interface State {
  userMandates: Array<DropdownOption>,
  specialRoles: Array<DropdownOption>,
  roles: Array<RoleDto>,
  epRoles: Array<EpRoleDto>
  userRoles: Array<UserRoleDto>,
  epUserRoles: Array<EpUserRoleDto>,
  errorMessage?: string,
  updateEpUserRoles: boolean,
  dataLoaded: boolean,
  cancelTokenSource: CancelTokenSource
}

class UpsertUserForm extends Component<Props, State> {

  initialValues = {};

  constructor(props: Props) {
    super(props);

    const {user} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      userMandates: [],
      roles: [],
      epRoles: [],
      specialRoles: [],
      userRoles: user ? user.userRoles : [],
      epUserRoles: user ? user.epUserRoles : [],
      updateEpUserRoles: false,
      dataLoaded: false,
      cancelTokenSource
    };

    this.initialValues = user ? user : {
      active: true,
      specialRoles: ["ROLE_USER", "ROLE_APP_USER"]
    };

    this.fetchData(props);
  }

  fetchData = (props: Props) => {

    const {cancelTokenSource} = this.state;
    const {getMessage} = props;

    const methods: Array<any> = [
      getAllMandates(cancelTokenSource),
      getAllRoles(cancelTokenSource),
      getAllEpRoles(cancelTokenSource),
      getAllAccountRoles(cancelTokenSource)
    ];

    Promise.all(methods)
        .then(responses => {
          this.setState({
            userMandates: mapToDropdownOptionArray(responses[0], "name", "id"),
            roles: responses[1],
            epRoles: responses[2],
            specialRoles: stringArrayToDropdownOptionArray(responses[3], getMessage)
          })
        })
        .catch(noop)
        .finally(() => {
          this.setState({
            dataLoaded: true
          })
        });
  }

  mapAccountRolesDropdownOptions = (list: Array<string>): Array<DropdownOption> => {

    return list.map((element) => {
      return {
        key: element,
        text: element,
        value: element
      }
    });
  };

  setSelectedUserRoles = (userRoles: Array<UserRoleDto>) => {
    this.setState({
      userRoles: userRoles
    })
  };

  setSelectedEpUserRoles = (epUserRoles: Array<EpUserRoleDto>) => {
    this.setState({
      epUserRoles: epUserRoles
    })
  };

  handleSubmit = async (
      values: Partial<UpsertUserSettingsDto>,
      form: FormApi) => {

    const {userRoles, epUserRoles, updateEpUserRoles, cancelTokenSource} = this.state;
    const {userId, accountId, getMessage, onSave} = this.props;

    let saveValues: Partial<UpsertUserSettingsDto> = {...values, userRoles, updateEpUserRoles};

    if (updateEpUserRoles) {
      saveValues.epUserRoles = epUserRoles;
    }

    saveValues.accountId = accountId;
    const file = form.getState().values.treaterSignatureFile;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (userId) {
      await editUser(userId, saveValues, file, cancelTokenSource)
          .then(onSave)
          .catch((e) => this.handleError(e.response))
    } else {
      await addUser(saveValues, file, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;

    const knownErrors = [errorUtils.userNotAuthorizedForEditOperation];
    let errorMessage = "";
    let message = error.data.message;

    let specificError = undefined;
    if (message != "") {
      specificError = knownErrors.includes(message) ? message : undefined;
    }

    if (specificError) {
      errorMessage = getMessage(`error.${specificError}`)
    } else {
      errorMessage = getMessage('error.general');
    }

    this.setErrorMessage(errorMessage)
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {user} = this.props;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
            validate={(values: Partial<Values>) => {
              const errors: Errors = {};
              if (!this.props.user) {
                if (values.password !== values.confirm) {
                  errors.confirm = "Must match";
                }
              }
              return errors;
            }}
        />
    );
  }

  renderForm = (
      {handleSubmit, submitting, values, errors, form}: FormRenderProps)
      : React.ReactNode => {

    const {roles, userMandates, userRoles, epRoles, epUserRoles, errorMessage, dataLoaded} = this.state;
    const {userId, accountId, getMessage, onCancel} = this.props;

    const mandateOptions: Array<DropdownOption> = userMandates.filter(mandate => {
      if (values.userMandates && values.userMandates.includes(mandate.value)) {
        return mandate;
      }
    });

    const selectedMandate: number =
        values.userMandates
        && values.userMandates.length > 0
            ? values.userMandates[0]
            : undefined;

    const currentUserRoles = this.props.currentUser ? this.props.currentUser.roles : [];
    const isSysAdmin = currentUserRoles.includes("ROLE_SYSTEM_ADMIN");

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <PraxFormScrollableContainer height="790px">
                  {errorMessage
                      && (
                          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                            {errorMessage}
                          </StyledErrorMessage>
                      )
                  }
                  {
                    !dataLoaded
                        ? <Dimmer active inverted>
                          <Loader inline="centered" size="medium">
                            {getMessage("loading")}
                          </Loader>
                        </Dimmer>
                        :
                        <PraxFormGrid>
                          {this.renderAccountDataSection()}
                          {this.renderPersonalDataSection(values, errors, form)}
                          {this.renderMandatesSection()}
                          {
                              isSysAdmin && (!userId || accountId) &&
                              this.renderSpecialRolesSection()
                          }
                          <UserRolesSegment
                              roles={roles}
                              userRoles={userRoles}
                              mandateOptions={mandateOptions}
                              selectedMandate={selectedMandate}
                              setSelectedUserRoles={this.setSelectedUserRoles}
                          />
                          <EpUserRolesSegment
                              epRoles={epRoles}
                              epUserRoles={epUserRoles}
                              mandateOptions={mandateOptions}
                              selectedMandate={selectedMandate}
                              setSelectedEpUserRoles={this.setSelectedEpUserRoles}
                              setUpdateUserEpRoles={this.setUpdateUserEpRoles}
                          />
                        </PraxFormGrid>
                  }
                </PraxFormScrollableContainer>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderAccountDataSection = (): React.ReactNode => {

    const {getMessage} = this.props;

    const validator = (value: string) => required(value);

    let userLeftInfoFields = [
      textInput({
        name: "username",
        validate: validator
      }),
      textInput({
        name: "email"
      })
    ];

    let userRightInfoFields = this.props.user
        ? []
        : [
          passwordInput({
            name: "password",
            validate: validator,
            type: "password"
          }),
          passwordInput({
            name: "confirm",
            type: "password"
          })
        ];

    const mappedLeftRow = this.renderPropertiesColumn(userLeftInfoFields, getMessage);

    const mappedRightRow = this.renderPropertiesColumn(userRightInfoFields, getMessage);

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={10} verticalAlign="middle">
              <Header>{getMessage("user.section.accountData")}</Header>
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("user.active")}
                  toggle
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8} style={{paddingLeft: 0}}>
              <PxGrid>
                {mappedLeftRow}
              </PxGrid>
            </Grid.Column>
            <Grid.Column width={8} style={{paddingRight: 0}}>
              <PxGrid>
                {mappedRightRow}
              </PxGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  }

  onDropAccepted = (acceptedFiles: File[], form: FormApi) => {
    form.change("treaterSignatureFile", acceptedFiles[0]);
  };

  onDropRejected = (rejections: FileRejection[]) => {
    let errors: FileError[] = [];
    rejections.forEach((rejection) => {
      errors = rejection.errors;
    })

    errors.forEach((error) => {
      this.setErrorMessage(error.message);
    });
  };

  renderPersonalDataSection = (
      values: Partial<UpsertUserSettingsDto>,
      errors: any,
      form: FormApi): React.ReactNode => {

    const {getMessage, treaters} = this.props;

    const validator = (value: string) => required(value);

    const selectMap: IndexSignature = {
      userId: treaters
    };

    let userLeftInfoFields = [
      textInput({
        name: "name",
        validate: validator
      }),
      textInput({
        name: "firstName"
      }),
      textInput({
        name: "title"
      }),
      textInput({
        name: "position"
      }),
      textInput({
        name: "eanNumber"
      }),
      textInput({
        name: "zsrNumber"
      }),
      textInput({
        name: "nif"
      })
    ];

    let userRightInfoFields = [
      textInput({
        name: "street"
      }),
      textInput({
        name: "city"
      }),
      textInput({
        name: "zip"
      }),
      textInput({
        name: "telephone"
      }),
      textInput({
        name: "fax"
      }),
      selectInput({
            name: "userId"
          },
          selectMap
      )
    ];

    const mappedLeftRow = this.renderPropertiesColumn(userLeftInfoFields, getMessage);

    const mappedRightRow = this.renderPropertiesColumn(userRightInfoFields, getMessage);

    const formValues = form.getState().values;
    const fileName = formValues.treaterSignatureFile
        ? formValues.treaterSignatureFile.name
        : formValues.treaterSignature;

    const uploadMessage = fileName
        ? fileName
        : getMessage("user.treaterSignature");

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={16} verticalAlign="middle">
              <Header>{getMessage("user.section.personalData")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8} style={{paddingLeft: 0}}>
              <PxGrid>
                {mappedLeftRow}
              </PxGrid>
            </Grid.Column>
            <Grid.Column width={8} style={{paddingRight: 0}}>
              <PxGrid>
                {mappedRightRow}
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("user.treaterSignature")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <DropZone
                        maxSize={1048576}
                        multiple={false}
                        message={uploadMessage}
                        onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles, form)}
                        onDropRejected={this.onDropRejected}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderMandatesSection = (): React.ReactNode => {

    const {userMandates} = this.state;
    const {getMessage} = this.props;

    const validator = (value: string) => required(value);

    let mandateFiledProps = selectInput({
      name: "userMandates",
      options: userMandates,
      validate: validator,
      multiple: true,
      fluid: true
    }, {});

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={16} verticalAlign="middle">
              <Header>{getMessage("user.userMandates")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <StyledField
                  fluid
                  {...mandateFiledProps}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  }

  renderSpecialRolesSection = (): React.ReactNode => {

    const {specialRoles} = this.state;
    const {getMessage} = this.props;

    const validator = (value: string) => required(value);

    let accountRolesFieldProps = selectInput({
      name: "specialRoles",
      options: specialRoles,
      validate: validator,
      multiple: true,
      fluid: true
    }, {});

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={16} verticalAlign="middle">
              <Header>{getMessage("user.specialRoles")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <StyledField
                  fluid
                  {...accountRolesFieldProps}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  }

  private renderPropertiesColumn(
      userRightInfoFields: (InputFieldProps | SelectFieldProps)[],
      getMessage: (key: string) => string): React.ReactNode {

    return userRightInfoFields.map((fieldProps, index) => {

      return (
          <React.Fragment key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {`${getMessage("user." + fieldProps.name)}:`}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </React.Fragment>
      );
    });
  }

  setUpdateUserEpRoles = (updateEpUserRoles: boolean) => {
    this.setState({updateEpUserRoles})
  }

}

export default withMessages(withAuthContext(UpsertUserForm));
